import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice Conversion Moules à Gâteau - Adapter les Quantités de Recette";
const DESCRIPTION =
  "Calculatrice Conversion Moules à Gâteau - Adapter les Quantités de Recette pour adapter vos recettes, portions et conversions en cuisine. Resultat rapide et pratique pour cuisiner avec plus de precision chaque jour.";
const KEYWORDS =
  "conversion moules à gâteau adapter les quantités de recette, calculatrice conversion moules à gâteau adapter les quantités de recette, calcul en ligne, cuisine, recette, conversion, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/cake-pan-conversion/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice Moules à Gâteau",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice Moules à Gâteau",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Comment convertir une recette de gâteau d'un moule rond à un moule rectangulaire ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Calculez la surface du moule d'origine et du nouveau, puis divisez la surface du nouveau moule par l'original pour obtenir le ratio d'ajustement. Multipliez chaque ingrédient de la recette par ce ratio.",
        },
      },
      {
        "@type": "Question",
        name: "Que se passe-t-il si j'utilise un moule plus grand sans adapter les quantités ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Le gâteau sera plus fin et sèchera probablement plus rapidement, car la pâte s'étalera sur une surface plus grande. Vous devez toujours adapter les quantités ou le temps cuisson en changeant la taille du moule.",
        },
      },
      {
        "@type": "Question",
        name: "La température change-t-elle en changeant la taille du moule ?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "La température reste généralement la même, mais le temps de cuisson change. Les moules plus grands ou plus profonds nécessitent un temps plus long, et les plus petits nécessitent un temps plus court. Vérifiez la cuisson avec un cure-dent.",
        },
      },
    ],
  },
];

// Pan shapes
const PAN_SHAPES = {
  round: "Rond",
  square: "Carré",
  rectangular: "Rectangulaire",
  bundt: "Bundt / Couronne",
  springform: "Springform Rond",
};

// Calculate pan area in cm²
const calcPanArea = (shape, d1, d2, depth) => {
  const h = parseFloat(depth) || 0;
  switch (shape) {
    case "round":
    case "springform": {
      const r = parseFloat(d1) / 2;
      return Math.PI * r * r * h;
    }
    case "square": {
      const s = parseFloat(d1);
      return s * s * h;
    }
    case "rectangular": {
      return parseFloat(d1) * parseFloat(d2) * h;
    }
    case "bundt": {
      const R = parseFloat(d1) / 2;
      const r = R * 0.35;
      return Math.PI * (R * R - r * r) * h;
    }
    default:
      return 0;
  }
};

function CakePanCalculator() {
  const [origShape, setOrigShape] = useState("round");
  const [origD1, setOrigD1] = useState("");
  const [origD2, setOrigD2] = useState("");
  const [origDepth, setOrigDepth] = useState("");

  const [newShape, setNewShape] = useState("square");
  const [newD1, setNewD1] = useState("");
  const [newD2, setNewD2] = useState("");
  const [newDepth, setNewDepth] = useState("");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const needsD2 = (shape) => shape === "rectangular";

  const getD1Label = (shape) => {
    if (shape === "round" || shape === "springform" || shape === "bundt") return "Diamètre (cm)";
    if (shape === "square") return "Longueur Côté (cm)";
    return "Longueur (cm)";
  };

  const calculateResult = () => {
    try {
      setError("");

      const oD1 = parseFloat(origD1);
      const oD2 = needsD2(origShape) ? parseFloat(origD2) : 0;
      const oDepth = parseFloat(origDepth);
      const nD1 = parseFloat(newD1);
      const nD2 = needsD2(newShape) ? parseFloat(newD2) : 0;
      const nDepth = parseFloat(newDepth);

      if (!oD1 || !oDepth || !nD1 || !nDepth) {
        setResult(null);
        return;
      }
      if (needsD2(origShape) && !oD2) {
        setResult(null);
        return;
      }
      if (needsD2(newShape) && !nD2) {
        setResult(null);
        return;
      }

      if (oD1 <= 0 || nD1 <= 0 || oDepth <= 0 || nDepth <= 0) {
        setError("Veuillez entrer des valeurs correctes et positives");
        setResult(null);
        return;
      }

      const origVolume = calcPanArea(origShape, oD1, oD2, oDepth);
      const newVolume = calcPanArea(newShape, nD1, nD2, nDepth);

      if (origVolume <= 0 || newVolume <= 0) {
        setError("Impossible de calculer le volume. Vérifiez les valeurs entrées.");
        setResult(null);
        return;
      }

      const ratio = newVolume / origVolume;

      let timeNote = "";
      if (ratio > 1.5) {
        timeNote = "La nouvelle recette est beaucoup plus grande — ajoutez 10-20 minutes au temps de cuisson et surveillez le gâteau";
      } else if (ratio > 1.1) {
        timeNote = "Le nouveau moule est légèrement plus grand — ajoutez 5-10 minutes au temps de cuisson";
      } else if (ratio < 0.7) {
        timeNote = "Le nouveau moule est beaucoup plus petit — réduisez le temps de cuisson d'environ 10-15 minutes";
      } else if (ratio < 0.9) {
        timeNote = "Le nouveau moule est légèrement plus petit — réduisez le temps de cuisson d'environ 5 minutes";
      } else {
        timeNote = "La taille du moule est très proche — vous pouvez garder le même temps cuisson approximativement";
      }

      setResult({
        ratio,
        origVolume,
        newVolume,
        origShapeName: PAN_SHAPES[origShape],
        newShapeName: PAN_SHAPES[newShape],
        timeNote,
        origDesc: origShape === "rectangular"
          ? `${oD1} × ${oD2} cm, profondeur ${oDepth} cm`
          : `${oD1} cm, profondeur ${oDepth} cm`,
        newDesc: newShape === "rectangular"
          ? `${nD1} × ${nD2} cm, profondeur ${nDepth} cm`
          : `${nD1} cm, profondeur ${nDepth} cm`,
      });
    } catch (err) {
      setError("Erreur Calcul: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    if (origD1 && origDepth && newD1 && newDepth) {
      calculateResult();
    } else {
      setResult(null);
      setError("");
    }
  }, [origShape, origD1, origD2, origDepth, newShape, newD1, newD2, newDepth]);

  const handleClear = () => {
    setOrigShape("round");
    setOrigD1("");
    setOrigD2("");
    setOrigDepth("");
    setNewShape("square");
    setNewD1("");
    setNewD2("");
    setNewDepth("");
    setResult(null);
    setError("");
  };

  const renderOrigInputs = () => (
    <>
      <div className="row">
        <div className={`col-sm-12 ${needsD2(origShape) ? "col-md-4" : "col-md-6"}`}>
          <label >{getD1Label(origShape)}</label>
          <input
            type="number"
            className="form-control"
            value={origD1}
            onChange={(e) => setOrigD1(e.target.value)}
            min="0"
            step="0.5"
            placeholder={getD1Label(origShape)}
          />
        </div>
        {needsD2(origShape) && (
          <div className="col-sm-12 col-md-4">
            <label >Largeur (cm)</label>
            <input
              type="number"
              className="form-control"
              value={origD2}
              onChange={(e) => setOrigD2(e.target.value)}
              min="0"
              step="0.5"
              placeholder="Largeur"
            />
          </div>
        )}
        <div className={`col-sm-12 ${needsD2(origShape) ? "col-md-4" : "col-md-6"}`}>
          <label >Profondeur / Hauteur (cm)</label>
          <input
            type="number"
            className="form-control"
            value={origDepth}
            onChange={(e) => setOrigDepth(e.target.value)}
            min="0"
            step="0.5"
            placeholder="Profondeur"
          />
        </div>
      </div>
    </>
  );

  const renderNewInputs = () => (
    <>
      <div className="row">
        <div className={`col-sm-12 ${needsD2(newShape) ? "col-md-4" : "col-md-6"}`}>
          <label >{getD1Label(newShape)}</label>
          <input
            type="number"
            className="form-control"
            value={newD1}
            onChange={(e) => setNewD1(e.target.value)}
            min="0"
            step="0.5"
            placeholder={getD1Label(newShape)}
          />
        </div>
        {needsD2(newShape) && (
          <div className="col-sm-12 col-md-4">
            <label >Largeur (cm)</label>
            <input
              type="number"
              className="form-control"
              value={newD2}
              onChange={(e) => setNewD2(e.target.value)}
              min="0"
              step="0.5"
              placeholder="Largeur"
            />
          </div>
        )}
        <div className={`col-sm-12 ${needsD2(newShape) ? "col-md-4" : "col-md-6"}`}>
          <label >Profondeur / Hauteur (cm)</label>
          <input
            type="number"
            className="form-control"
            value={newDepth}
            onChange={(e) => setNewDepth(e.target.value)}
            min="0"
            step="0.5"
            placeholder="Profondeur"
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice Moules Gâteau</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            {/* Introduction */}
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Calculatrice Moules Gâteau</strong> résout le problème de chacun qui aime la pâtisserie : que faire si ma recette est écrite pour un moule rond de 20 cm mais j'ai un moule rectangulaire ? Entrez les dimensions de votre moule d'origine et du nouveau, et obtenez immédiatement le <strong>ratio d'ajustement des ingrédients</strong> avec précision. Pas de deviner, pas de gâteau raté.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            {/* Calculator Section */}
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Convertissez votre Recette Entre les Moules Facilement
                </h2>
              </div>

              {/* Original Pan */}
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#1a5f3f" }}>
                  🎂 Moule d'Origine (Moule Recette)
                </h3>
                <div className="row">
                  <div className="col-sm-12">
                    <label >Forme du Moule</label>
                    <select
                      className="form-select"
                      value={origShape}
                      onChange={(e) => {
                        setOrigShape(e.target.value);
                        setOrigD2("");
                      }}
                    >
                      {Object.entries(PAN_SHAPES).map(([key, val]) => (
                        <option key={key} value={key}>{val}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {renderOrigInputs()}
              </div>

              {/* New Pan */}
              <div
                style={{
                  backgroundColor: "#fff8f0",
                  border: "1px solid #ffc98b",
                  borderRadius: "8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#8a4500" }}>
                  🍰 Nouveau Moule (Celui que vous Utiliserez)
                </h3>
                <div className="row">
                  <div className="col-sm-12">
                    <label >Forme du Moule</label>
                    <select
                      className="form-select"
                      value={newShape}
                      onChange={(e) => {
                        setNewShape(e.target.value);
                        setNewD2("");
                      }}
                    >
                      {Object.entries(PAN_SHAPES).map(([key, val]) => (
                        <option key={key} value={key}>{val}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {renderNewInputs()}
              </div>

              {/* Buttons */}
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  Effacer
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Alerte :</strong> {error}
                </div>
              )}

              {/* Results */}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>Résultat :</strong>
                  </h3>
                  {/* Main Results Table */}
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            Description
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            Valeur
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Ratio d'Ajustement des Quantités</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "22px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            × {result.ratio.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Volume Moule d'Origine</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {result.origVolume.toFixed(1)} cm³
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Volume Nouveau Moule</td>
                          <td style={{ textAlign: "center", fontSize: "16px" }}>
                            {result.newVolume.toFixed(1)} cm³
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Différence de Volume</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "16px",
                              color: result.ratio > 1 ? "#0f3d28" : "#8a0000",
                            }}
                          >
                            {result.ratio > 1
                              ? `+${((result.ratio - 1) * 100).toFixed(0)}%`
                              : `${((result.ratio - 1) * 100).toFixed(0)}%`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Comment Appliquer le Ratio ?</strong>
                      </div>
                      <div className="info-section-body">
                        <p style={{ lineHeight: "1.8" }}>
                          Multipliez chaque ingrédient de votre recette d'origine par le facteur{" "}
                          <strong
                            style={{ fontSize: "18px", color: "#1a5f3f" }}
                          >
                            {result.ratio.toFixed(2)}
                          </strong>
                        </p>
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2.2" }}>
                          <li>🥚 <strong>Exemple — 3 Œufs :</strong> {(3 * result.ratio).toFixed(1)} œuf(s)</li>
                          <li>🧈 <strong>Exemple — 200 g Beurre :</strong> {(200 * result.ratio).toFixed(0)} g</li>
                          <li>🍚 <strong>Exemple — 250 g Farine :</strong> {(250 * result.ratio).toFixed(0)} g</li>
                          <li>🍬 <strong>Exemple — 150 g Sucre :</strong> {(150 * result.ratio).toFixed(0)} g</li>
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>💡 Remarque :</strong> Pour les œufs, si le résultat est une fraction comme 2.4, utilisez 2 œufs et ajoutez une partie du 3e œuf battu.
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Détails du Calcul et Conseil de Cuisson</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>Moule d'Origine :</strong> {result.origShapeName} — {result.origDesc}
                          </p>
                          <p>
                            <strong>Nouveau Moule :</strong> {result.newShapeName} — {result.newDesc}
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#555" }}>
                            <strong>Calcul :</strong> Ratio d'Ajustement = Volume Nouveau Moule ÷ Volume Moule d'Origine
                            <br />
                            = {result.newVolume.toFixed(1)} ÷ {result.origVolume.toFixed(1)} = <strong>{result.ratio.toFixed(3)}</strong>
                          </p>
                          <hr />
                          <div className="alert alert-warning mt-2" style={{ fontSize: "14px" }}>
                            <strong>⏱️ Temps Cuisson :</strong> {result.timeNote}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Related Links */}
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Qu'est-ce que la Calculatrice Moules à Gâteau ?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  C'est un outil intelligent qui calcule pour vous le ratio d'ajustement des ingrédients du gâteau automatiquement en changeant la taille ou la forme du moule. L'idée est simple : chaque moule a un volume (surface × profondeur), et quand vous basculez entre deux moules différents, les quantités de pâte changent proportionnellement au ratio du volume.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Comment Utiliser la Calculatrice :
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Choisissez la forme du moule d'origine (celui dans la recette) et entrez ses dimensions</li>
                  <li>Choisissez la forme du nouveau moule que vous utiliserez et entrez ses dimensions</li>
                  <li>Le résultat s'affiche immédiatement : le multiplicateur à appliquer à chaque ingrédient</li>
                  <li>Lisez le conseil de temps de cuisson à la fin des résultats</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 Remarque :</strong> La calculatrice fonctionne avec les moules ronds, carrés, rectangulaires, Bundt et ressorts. Entrez les dimensions en centimètres pour obtenir les résultats les plus précis.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Utilisations de la Calculatrice Moules Gâteau</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  En Cuisine À la Maison :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Agrandir une Recette pour Plus Invités :</strong> Convertissez une recette moule 20 cm en moule 26 cm pour plus de convives
                  </li>
                  <li>
                    <strong>Réduire la Recette :</strong> Vous voulez un gâteau pour deux personnes ? Convertissez les quantités pour un moule plus petit
                  </li>
                  <li>
                    <strong>Utiliser Ce Que Vous Avez :</strong> Ne trouvez pas le moule demandé ? Entrez celui que vous avez et obtenez les bonnes quantités
                  </li>
                  <li>
                    <strong>Essayer de Nouvelles Formes :</strong> Convertissez une recette ronde en moule Bundt pour varier
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Pour les Boulangeries et Pâtisseries Professionnelles :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Normalisation des Tailles :</strong> Production de gâteaux à tailles précises de manière cohérente
                  </li>
                  <li>
                    <strong>Calculs de Coûts :</strong> Connaître les quantités précises aide à évaluer les produits finaux
                  </li>
                  <li>
                    <strong>Exécution de Commandes Personnalisées :</strong> Client demande un gâteau de taille inhabituelle ? Calculez les quantités avec confiance
                  </li>
                  <li>
                    <strong>Formation des Employés :</strong> Outil de référence rapide pour les cuisiniers novices
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Pour les Amateurs de Pâtisserie et d'Apprentissage :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Comprendre la Relation Taille-Quantités :</strong> Apprenez les mathématiques de manière amusante
                  </li>
                  <li>
                    <strong>Éviter le Gaspillage :</strong> Fini les gâteaux excédentaires ou insuffisants
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Surfaces de Moules Courantes et Comparaisons</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Connaître la surface du moule est la base du calcul. Vous pouvez utiliser le tableau ci-dessous comme référence rapide pour les moules les plus courants (profondeur 5 cm) :
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Type de Moule</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Dimensions</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Volume Approximatif (cm³)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Rond</td>
                      <td style={{ textAlign: "center" }}>20 cm diamètre</td>
                      <td style={{ textAlign: "center" }}>1,571</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Rond</td>
                      <td style={{ textAlign: "center" }}>23 cm diamètre</td>
                      <td style={{ textAlign: "center" }}>2,083</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Rond</td>
                      <td style={{ textAlign: "center" }}>26 cm diamètre</td>
                      <td style={{ textAlign: "center" }}>2,659</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Carré</td>
                      <td style={{ textAlign: "center" }}>20 × 20 cm</td>
                      <td style={{ textAlign: "center" }}>2,000</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Rectangulaire</td>
                      <td style={{ textAlign: "center" }}>30 × 20 cm</td>
                      <td style={{ textAlign: "center" }}>3,000</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Rectangulaire</td>
                      <td style={{ textAlign: "center" }}>33 × 23 cm</td>
                      <td style={{ textAlign: "center" }}>3,795</td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Comment Calculer la Surface Manuellement ?
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Rond :</strong> π × Rayon² × Profondeur</li>
                  <li><strong>Carré :</strong> Côté² × Profondeur</li>
                  <li><strong>Rectangulaire :</strong> Longueur × Largeur × Profondeur</li>
                  <li><strong>Bundt :</strong> π × (Rayon Externe² − Rayon Interne²) × Profondeur</li>
                </ul>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ Astuce :</strong> Un moule carré 20 cm équivaut à peu près à un moule rond 23 cm en volume — une info qui vous sauve la mise à la cuisine.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Exemples Pratiques de Conversion Moules</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Exemple 1 : De Rond 20 cm À Rond 25 cm
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situation :</strong> Votre recette est pour un moule rond diamètre 20 cm profondeur 5 cm, et vous voulez utiliser 25 cm.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Calcul :</strong><br />
                  Volume Moule d'Origine = π × 10² × 5 = 1,571 cm³<br />
                  Volume Nouveau Moule = π × 12.5² × 5 = 2,454 cm³<br />
                  Ratio d'Ajustement = 2,454 ÷ 1,571 = <strong>1.56</strong><br />
                  Multipliez tous les ingrédients × 1.56
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Exemple 2 : De Rectangulaire À Rond
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situation :</strong> Votre recette est pour moule rectangulaire 33 × 23 cm profondeur 5 cm, convertir en rond 26 cm.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Calcul :</strong><br />
                  Volume Rectangulaire = 33 × 23 × 5 = 3,795 cm³<br />
                  Volume Rond = π × 13² × 5 = 2,659 cm³<br />
                  Ratio d'Ajustement = 2,659 ÷ 3,795 = <strong>0.70</strong><br />
                  Utilisez 70% seulement des quantités de la recette
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Exemple 3 : Agrandissement pour les Occasions
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Situation :</strong> Recette moule carré 20 × 20 cm profondeur 5 cm, convertir en rectangulaire 40 × 30 cm.
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Calcul :</strong><br />
                  Volume Carré = 20 × 20 × 5 = 2,000 cm³<br />
                  Volume Rectangulaire = 40 × 30 × 5 = 6,000 cm³<br />
                  Ratio d'Ajustement = 6,000 ÷ 2,000 = <strong>3.0</strong><br />
                  Tripler tous les ingrédients
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Conseils pour Réussir la Conversion Moules</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Conseils Essentiels :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Mesurez de l'intérieur du Moule :</strong> Les dimensions écrites sur le moule viennent parfois du bord extérieur
                  </li>
                  <li>
                    <strong>Vérifiez le Gâteau tôt :</strong> Si vous agrandissez le moule, commencez à vérifier la cuisson 10 min avant le temps prédéfini
                  </li>
                  <li>
                    <strong>Ne Remplissez pas Plus des Deux Tiers :</strong> La pâte monte à la cuisson
                  </li>
                  <li>
                    <strong>La Température ne Change Pas :</strong> Seul le temps de cuisson est affecté par le changement de taille
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  Pour Éviter les Erreurs :
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Entrez toutes les mesures dans la même unité (cm)</li>
                  <li>N'oubliez pas la profondeur — c'est une partie essentielle du calcul</li>
                  <li>Si le multiplicateur est plus grand que 2, pensez à cuire la recette en deux lots</li>
                  <li>Pour les œufs, arrondissez aux nombres entiers les plus proches ou utilisez une fraction d'œuf battu</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CakePanCalculator;